import { useTranslation } from 'react-i18next'
import { Check, ChevronDown, Gauge, Info, Lock } from '@digital-twins/design-system/icons'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { assistantTypicalCost } from '@/lib/billing/metering'
import { cn } from '@/lib/utils'

import {
  ComposerAppearance,
  composerTriggerCaptionClassName,
  composerTriggerChevronClassName,
  composerTriggerClassName,
} from './composerAppearance'

import type { AssistantProfileOption } from '@/lib/api'

/**
 * The Assistant profile picker (plan 2026-10-21, Step A2, D1 and D3).
 *
 * Sits where `EffortToggle` sat in both Assistant composers and replaces it there: a profile
 * decides the effort, so the two controls cannot both be live. Presentation follows the room
 * `ConversationModelPicker` (same design-system `DropdownMenu`, same trigger classes, levels
 * first and a locked Advanced submenu), but none of its mapping or pricing: Assistant turns
 * cost zero credits (D4), so no price is shown. For an enrolled account (credit metering) the
 * server sends each public level's `pricing`; the menu then shows its typical cost per message
 * ("Medium · under 1 credit per message") and an (i) with the token types. Never per-million
 * wording, never a fixed price, never a confirmation step.
 *
 * Every viewer sees Low, Medium and High. Staff profiles arrive in `options` only for verified
 * Fairgen staff (the server filters them) and are listed under Advanced with a plain label
 * from the id, never a customer i18n key. Keyboard selection is the menu's own (arrows, Enter).
 * Renders nothing until the list has loaded.
 */

const PUBLIC_LABEL_KEYS = {
  low: 'assistantProfile.low',
  medium: 'assistantProfile.medium',
  high: 'assistantProfile.high',
} as const

type PublicProfileId = keyof typeof PUBLIC_LABEL_KEYS

function isPublicProfileId(profileId: string): profileId is PublicProfileId {
  return Object.prototype.hasOwnProperty.call(PUBLIC_LABEL_KEYS, profileId)
}

export interface AssistantProfilePickerProps {
  /** The effective profile id (`useAssistantProfile().profileId`). */
  value: string | null
  /** Selectable profiles in registry order, or null while loading. */
  options: Array<AssistantProfileOption> | null
  onChange: (profileId: string) => void
  appearance?: ComposerAppearance
  disabled?: boolean
  className?: string
  /** Prefix of every data-testid (`-trigger`, `-option-<id>`, `-advanced`). */
  testIdPrefix?: string
}

export function AssistantProfilePicker(props: AssistantProfilePickerProps) {
  const {
    value,
    options,
    onChange,
    appearance = 'external',
    disabled = false,
    className,
    testIdPrefix = 'assistant-profile',
  } = props

  const { t, i18n } = useTranslation('app')

  if (!options || options.length === 0) {
    return null
  }

  const publicOptions = options.filter((option) => option.audience === 'public')
  const staffOptions = options.filter((option) => option.audience === 'staff')

  const hasTypicalCost = publicOptions.some((option) => option.pricing)

  function typicalLineFor(option: AssistantProfileOption): string | null {
    const typical = assistantTypicalCost(option.pricing?.typicalCreditsPerMessage)
    if (!typical) {
      return null
    }
    const amount = new Intl.NumberFormat(i18n?.language || 'en', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }).format(typical)
    return t('assistantProfile.typicalLine', { amount })
  }

  function labelFor(profileId: string): string {
    if (isPublicProfileId(profileId)) {
      return t(PUBLIC_LABEL_KEYS[profileId])
    }
    return profileId
  }

  const activeLabel = value ? labelFor(value) : t('assistantProfile.medium')
  const isStaffActive = staffOptions.some((option) => option.id === value)

  /* Fixed-width slot after the price: empty on unselected rows so every price lines up. */
  const checkSlot = (profileId: string) => (
    <span className="flex h-4 w-4 shrink-0 items-center justify-center" data-testid={`${testIdPrefix}-check-${profileId}`}>
      {profileId === value ? <Check className="h-4 w-4 text-primary" aria-hidden="true" /> : null}
    </span>
  )

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={`${t('assistantProfile.label')}: ${activeLabel}`}
        disabled={disabled}
        data-testid={`${testIdPrefix}-trigger`}
        className={cn(
          composerTriggerClassName(appearance),
          'shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
          className,
        )}
      >
        {isStaffActive ? (
          <Lock className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        ) : (
          <Gauge className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        )}
        <span
          className={composerTriggerCaptionClassName(appearance)}
          data-testid={`${testIdPrefix}-trigger-label`}
        >
          {activeLabel}
        </span>
        <ChevronDown
          className={cn('h-3 w-3 shrink-0 opacity-70', composerTriggerChevronClassName(appearance))}
          aria-hidden="true"
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        side="top"
        collisionPadding={8}
        className="min-w-[13rem]"
        data-testid={`${testIdPrefix}-menu`}
      >
        {publicOptions.map((option) => {
          const selected = option.id === value
          const typicalLine = typicalLineFor(option)
          return (
            <DropdownMenuItem
              key={option.id}
              onSelect={() => onChange(option.id)}
              data-testid={`${testIdPrefix}-option-${option.id}`}
              aria-checked={selected}
              className={cn(
                'gap-3 focus-visible:ring-2 focus-visible:ring-ring',
                selected && 'bg-accent/50 font-medium',
              )}
            >
              <span className="flex-1">{labelFor(option.id)}</span>
              {typicalLine ? (
                <span
                  className={cn(
                    'text-xs font-normal tabular-nums',
                    selected ? 'text-foreground' : 'text-muted-foreground',
                  )}
                  data-testid={`${testIdPrefix}-option-${option.id}-typical`}
                >
                  {typicalLine}
                </span>
              ) : null}
              {checkSlot(option.id)}
            </DropdownMenuItem>
          )
        })}
        {hasTypicalCost ? (
          <div className="flex items-center gap-1 px-2 py-1.5 text-xs text-muted-foreground" data-testid={`${testIdPrefix}-pricing-info`}>
            <span data-testid={`${testIdPrefix}-pricing-footer`}>{t('assistantProfile.typicalFooter')}</span>
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label={t('assistantProfile.pricingInfoLabel')}
                    data-testid={`${testIdPrefix}-pricing-info-trigger`}
                    className="rounded-full p-0.5 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <Info className="h-3.5 w-3.5" aria-hidden="true" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right" className="max-w-[240px] text-xs">
                  {t('assistantProfile.pricingInfo')}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        ) : null}
        {staffOptions.length > 0 ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuSub>
              <DropdownMenuSubTrigger data-testid={`${testIdPrefix}-advanced`} className="gap-2">
                <Lock className="h-3.5 w-3.5" aria-hidden="true" />
                <span>{t('assistantProfile.advanced')}</span>
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent
                data-testid={`${testIdPrefix}-advanced-content`}
                avoidCollisions
                collisionPadding={8}
                className="w-max min-w-0"
              >
                {staffOptions.map((option) => (
                  <DropdownMenuItem
                    key={option.id}
                    onSelect={() => onChange(option.id)}
                    data-testid={`${testIdPrefix}-option-${option.id}`}
                    className={cn('gap-3', option.id === value && 'bg-accent/50 font-medium')}
                  >
                    <span className="flex-1">{option.id}</span>
                    {checkSlot(option.id)}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
